import React from 'react';
import { Button } from '@radix-ui/themes';
import { CSVExportButtonProps, SubmissionCSVExportButtonProps } from './types';
import { 
  convertQuestionsToCSV, 
  downloadCSV, 
  filterSelectedQuestions,
  convertSubmissionsToCSV,
  filterSelectedSubmissions 
} from './csvUtils';

// Existing Questions CSV Export Button
const CSVExportButton: React.FC<CSVExportButtonProps> = ({
  questions,
  selectedQuestions,
  filename,
  className = '',
  variant = 'all',
  disabled = false
}) => {
  const handleExport = () => {
    try {
      let questionsToExport = questions;
      
      // Filter questions if variant is 'selected'
      if (variant === 'selected') {
        questionsToExport = filterSelectedQuestions(questions, selectedQuestions);
        
        // Check if any questions are selected
        if (questionsToExport.length === 0) {
          alert('Please select at least one question to export.');
          return;
        }
      }
      
      // Generate CSV content
      const csvContent = convertQuestionsToCSV(questionsToExport);
      
      // Generate filename
      const defaultFilename = variant === 'selected' 
        ? `selected-questions-${new Date().toISOString().split('T')[0]}.csv`
        : `all-questions-${new Date().toISOString().split('T')[0]}.csv`;
      
      const finalFilename = filename || defaultFilename;
      
      // Download the file
      downloadCSV(csvContent, finalFilename);
      
      // Show success message
      const questionCount = questionsToExport.length;
      const message = variant === 'selected' 
        ? `Successfully exported ${questionCount} selected question${questionCount !== 1 ? 's' : ''} to CSV.`
        : `Successfully exported all ${questionCount} question${questionCount !== 1 ? 's' : ''} to CSV.`;
      
      alert(message);
      
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Failed to export questions to CSV. Please try again.');
    }
  };

  const isDisabled = disabled || questions.length === 0 || 
    (variant === 'selected' && selectedQuestions.length === 0);

  const buttonText = variant === 'selected' 
    ? `Export Selected (${selectedQuestions.length})`
    : `Export All (${questions.length})`;

  const buttonIcon = variant === 'selected' ? '📋' : '📊';

  return (
    <Button
      onClick={handleExport}
      disabled={isDisabled}
      size="3"
      variant="solid"
      className={`bg-green-600 hover:bg-green-700 text-white px-6 ${className}`}
      style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
    >
      {buttonIcon} {buttonText}
    </Button>
  );
};

// New Submissions CSV Export Button
const SubmissionCSVExportButton: React.FC<SubmissionCSVExportButtonProps> = ({
  submissions,
  selectedSubmissions,
  filename,
  className = '',
  variant = 'all',
  disabled = false
}) => {
  const handleExport = () => {
    try {
      let submissionsToExport = submissions;
      
      // Filter submissions if variant is 'selected'
      if (variant === 'selected') {
        submissionsToExport = filterSelectedSubmissions(submissions, selectedSubmissions);
        
        // Check if any submissions are selected
        if (submissionsToExport.length === 0) {
          alert('Please select at least one submission to export.');
          return;
        }
      }
      
      // Generate CSV content
      const csvContent = convertSubmissionsToCSV(submissionsToExport);
      
      // Generate filename
      const defaultFilename = variant === 'selected' 
        ? `selected-submissions-${new Date().toISOString().split('T')[0]}.csv`
        : `all-submissions-${new Date().toISOString().split('T')[0]}.csv`;
      
      const finalFilename = filename || defaultFilename;
      
      // Download the file
      downloadCSV(csvContent, finalFilename);
      
      // Show success message
      const submissionCount = submissionsToExport.length;
      const message = variant === 'selected' 
        ? `Successfully exported ${submissionCount} selected submission${submissionCount !== 1 ? 's' : ''} to CSV.`
        : `Successfully exported all ${submissionCount} submission${submissionCount !== 1 ? 's' : ''} to CSV.`;
      
      alert(message);
      
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Failed to export submissions to CSV. Please try again.');
    }
  };

  const isDisabled = disabled || submissions.length === 0 || 
    (variant === 'selected' && selectedSubmissions.length === 0);

  const buttonText = variant === 'selected' 
    ? `Export Selected (${selectedSubmissions.length})`
    : `Export All (${submissions.length})`;

  const buttonIcon = variant === 'selected' ? '📋' : '📊';

  return (
    <Button
      onClick={handleExport}
      disabled={isDisabled}
      size="3"
      variant="solid"
      className={`bg-green-600 hover:bg-green-700 text-white px-6 ${className}`}
      style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
    >
      {buttonIcon} {buttonText}
    </Button>
  );
};

export default CSVExportButton;
export { SubmissionCSVExportButton }; 